import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getPrintJobs, getPrinters, retryPrintJob, deletePrintJob } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SearchInput from "../components/UI/SearchInput";
import SelectBox from "../components/UI/SelectBox";
import ConfirmModal from "../components/UI/ConfirmModal";
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

// PRINT_JOB_STATUS_BADGE mirrors models.PrintJob's own status constants
// (see print_job.go's own doc comment on each): pending/sent are both
// "in progress" in different senses (not yet handed to the agent, vs.
// handed to it and awaiting an ack), acked is the terminal success state,
// failed is terminal-and-bad (after maxPrintJobAttempts, or a stale "sent"
// job the agent never acked in time).
const PRINT_JOB_STATUS_BADGE = {
  pending: "bg-amber-50 text-amber-600",
  sent: "bg-blue-50 text-blue-600",
  acked: "bg-green-50 text-green-600",
  failed: "bg-red-50 text-red-500",
};

// PrintJobs.js is the admin's own visibility/control over the PrintJob
// queue (see internal/handlers/print_job_handler.go's own doc comments on
// the backend) — what's stuck, what failed and why, with a Retry action
// for a Failed job and a Delete action for any of them. This is also the
// ONLY way to clear a printer's "still has print jobs on record"
// delete-block (see DeletePrinterHandler's own doc comment) — Printers.js
// links here with ?printer_id= preset the moment that block is hit, rather
// than leaving the admin at a dead end.
export default function PrintJobs() {
  const { t } = useTranslation("print_jobs");
  const { t: tCommon } = useTranslation("common");
  const [searchParams, setSearchParams] = useSearchParams();
  const printerFilter = searchParams.get("printer_id") || "";
  const statusFilter = searchParams.get("status") || "";

  const [jobs, setJobs] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });
  const [retryingId, setRetryingId] = useState(null);
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
  const [deleteStatus, setDeleteStatus] = useState("idle");

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchJobs = async () => {
    try {
      const params = {};
      if (printerFilter) params.printer_id = printerFilter;
      if (statusFilter) params.status = statusFilter;
      const data = await getPrintJobs(token, params);
      setJobs(data || []);
    } catch (err) {
      notify.error(t("notify_load_failed", "Failed to load print jobs"));
    }
  };

  useEffect(() => {
    getPrinters(token).then((data) => setPrinters(data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchJobs(); }, [printerFilter, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateParams = (newParams) => {
    const current = Object.fromEntries(searchParams.entries());
    const merged = { ...current, ...newParams };
    Object.keys(merged).forEach((k) => { if (!merged[k]) delete merged[k]; });
    setSearchParams(merged);
  };

  const printerOptions = useMemo(() => [
    { value: "", label: t("filter_printer_all_option", "All printers") },
    ...printers.map((p) => ({ value: String(p.id), label: `${p.name} (${p.print_agent?.branch?.name || "—"})` })),
  ], [printers, t]);

  const statusOptions = [
    { value: "", label: t("filter_status_all_option", "All statuses") },
    { value: "pending", label: t("status_pending", "Pending") },
    { value: "sent", label: t("status_sent", "Sent") },
    { value: "acked", label: t("status_acked", "Acked") },
    { value: "failed", label: t("status_failed", "Failed") },
  ];

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    let result = jobs.filter((j) =>
      !term ||
      String(j.order_id).includes(term) ||
      j.order?.invoice_number?.toLowerCase().includes(term) ||
      j.printer?.name?.toLowerCase().includes(term)
    );
    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [jobs, searchTerm, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const handleRetry = async (job) => {
    try {
      setRetryingId(job.id);
      await retryPrintJob(token, job.id);
      notify.success(t("notify_retried", "Print job queued for another attempt"));
      fetchJobs();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_retry_failed", "Failed to retry print job"));
    } finally {
      setRetryingId(null);
    }
  };

  const handleDeleteClick = (id) => setModalConfig({ isOpen: true, id });

  const confirmDelete = async () => {
    try {
      setDeleteStatus("loading");
      await deletePrintJob(token, modalConfig.id);
      setDeleteStatus("success");
      setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setDeleteStatus("idle");
      }, 1200);
      notify.success(t("notify_deleted", "Print job deleted"));
      fetchJobs();
    } catch (err) {
      setDeleteStatus("error");
      notify.error(err.data?.error || err.message || t("notify_delete_failed", "Failed to delete print job"));
    }
  };

  const columns = [
    { key: "id", label: tCommon("table_col_id", "ID"), className: "text-gray-400 font-mono" },
    {
      key: "order_id",
      label: t("col_order", "Order"),
      className: "font-bold text-gray-700",
      render: (row) => `#${row.order?.invoice_number || row.order_id}`,
    },
    {
      key: "printer",
      label: t("col_printer", "Printer"),
      className: "text-gray-600",
      render: (row) => (
        <div>
          <div className="font-medium">{row.printer?.name || "—"}</div>
          <div className="text-[10px] text-gray-400 font-mono">{row.printer?.host}:{row.printer?.port}</div>
        </div>
      ),
    },
    {
      key: "branch",
      label: t("col_branch", "Branch"),
      className: "text-gray-600",
      render: (row) => row.printer?.print_agent?.branch?.name || "—",
    },
    {
      key: "status",
      label: tCommon("table_col_status", "Status"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${PRINT_JOB_STATUS_BADGE[row.status] || "bg-gray-100 text-gray-500"}`}>
          {t(`status_${row.status}`, row.status)}
        </span>
      ),
    },
    { key: "attempts", label: t("col_attempts", "Attempts"), className: "text-gray-500 text-center" },
    {
      key: "trigger",
      label: t("col_trigger", "Trigger"),
      className: "text-gray-500",
      render: (row) => t(`trigger_${row.trigger}`, row.trigger),
    },
    {
      key: "last_error",
      label: t("col_last_error", "Last Error"),
      className: "text-red-400 text-xs max-w-[220px] truncate",
      render: (row) => <span title={row.last_error || ""}>{row.last_error || "—"}</span>,
    },
    {
      key: "created_at",
      label: t("col_created_at", "Created"),
      className: "text-gray-500 text-xs",
      render: (row) => (row.created_at ? new Date(row.created_at).toLocaleString() : "—"),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={t("delete_confirm_title", "Delete Print Job")}
        message={t("delete_confirm_message", "Are you sure? This permanently removes the job record — it cannot be recovered.")}
        onConfirm={confirmDelete}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
        isLoading={deleteStatus === "loading"}
        isSuccess={deleteStatus === "success"}
        isError={deleteStatus === "error"}
      />

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.PrintJobs size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Print Jobs")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Every queued, sent, printed, or failed receipt — retry a failed one, or delete a job outright")}</p>
          </div>
        </div>

        <SearchInput
          value={searchTerm}
          onChange={(val) => setSearchTerm(val)}
          placeholder={t("search_placeholder", "Search order # or printer...")}
        />
      </PageHeader>

      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SelectBox
          label={t("filter_printer_label", "Filter by printer")}
          options={printerOptions}
          value={printerFilter}
          onChange={(val) => updateParams({ printer_id: val })}
        />
        <SelectBox
          label={t("filter_status_label", "Filter by status")}
          options={statusOptions}
          value={statusFilter}
          onChange={(val) => updateParams({ status: val })}
        />
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) => (
          <>
            {row.status === "failed" && (
              <Button
                variant="outline"
                size="sm"
                icon={ICONS.Refresh}
                onClick={() => handleRetry(row)}
                disabled={retryingId === row.id}
              >
                {isMobile && ` ${t("btn_retry", "Retry")}`}
              </Button>
            )}
            <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => handleDeleteClick(row.id)}>
              {isMobile && ` ${tCommon("btn_delete", "Delete")}`}
            </Button>
          </>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">#{row.id}</span>
            <h3 className="font-bold text-gray-800">{row.printer?.name || "—"}</h3>
          </div>
        )}
      />
    </div>
  );
}
